import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { PromoCodesManager } from '@ctct/application-layer';
import { type BillingStrategyQuery } from '@ctct/domain-layer';
import type {
  EmailPlansHooks,
  OrderSummaryHooks,
  PrepaySelectHooks,
  PromoCodesHooks,
  PromoCodesPageStore,
} from '@ctct/state-client-layer';
import {
  useCustomerInfo,
  usePlanDetail,
  usePromoValidation,
  useRating,
  usePromoCodeVisibilityFacade,
} from '@ctct/state-server-layer';

import { useWidgetLifecycle } from '../../hooks/useWidgetLifecycle';
import { resolveBillingStrategyQuery } from '../helpers/resolveStrategyQuery';

interface Props {
  initializer: Pick<PromoCodesManager, 'promoCodesInit'>;
  pageStore: PromoCodesPageStore;
  hooks: PromoCodesHooks;
  externalHooks: {
    useRatingCallPayload: OrderSummaryHooks['useRatingCallPayload'];
    useSelectedEmailPlanId: EmailPlansHooks['useSelectedEmailPlanId'];
    usePrepay: PrepaySelectHooks['usePrepay'];
  };
  billingStrategy: BillingStrategyQuery;
  isCompact?: boolean;
  hideButtonWhenInputIsEmpty?: boolean;
}

export const usePromoCodesWidget = ({
  initializer,
  pageStore,
  hooks,
  externalHooks,
  billingStrategy,
  isCompact,
  hideButtonWhenInputIsEmpty,
}: Props) => {
  const { t } = useTranslation();
  const selectedEmailPlanId = externalHooks.useSelectedEmailPlanId();
  const paymentPeriod = externalHooks.usePrepay();

  const { shouldHidePromoCodes, data: isHidePromoCodesFeatureGateEnabled } =
    usePromoCodeVisibilityFacade(paymentPeriod);

  const ratingPayload = externalHooks.useRatingCallPayload();
  const ratingQuery = useRating(ratingPayload);
  const resolvedBillingStrategy = resolveBillingStrategyQuery(billingStrategy);
  const promoValidationQuery = usePromoValidation();
  const planDetailQuery = usePlanDetail();
  const customer = useCustomerInfo();

  const componentProps = useMemo(
    () => ({
      ...initializer.promoCodesInit({
        pageStore,
        t,
        queries: [promoValidationQuery, ratingQuery, planDetailQuery, customer],
        externalStateValues: {
          selectedEmailPlanId,
          paymentPeriod,
          ratingRequest: ratingPayload,
        },
        billingStrategy: resolvedBillingStrategy,
        shouldHidePromoCodes,
        isHidePromoCodesFeatureGateEnabled,
      }),
      hooks,
      isCompact,
      hideButtonWhenInputIsEmpty,
    }),
    [
      initializer,
      pageStore,
      t,
      promoValidationQuery,
      hooks,
      ratingQuery,
      selectedEmailPlanId,
      paymentPeriod,
      planDetailQuery,
      customer,
      resolvedBillingStrategy,
      ratingPayload,
      isCompact,
      hideButtonWhenInputIsEmpty,
      shouldHidePromoCodes,
      isHidePromoCodesFeatureGateEnabled,
    ]
  );

  useWidgetLifecycle(componentProps.status, componentProps.lifecycle);

  return componentProps;
};

export type PromoCodesWidgetProps = ReturnType<typeof usePromoCodesWidget>;
